import type { Environment } from './models';

export interface EnvValidationResult {
    criticalErrors: string[];
    blockingErrors: string[];
    warnings: string[];
}

type Tier = 'critical' | 'blocking' | 'warning';

/**
 * Generates dot-path keys for a type, treating arrays and featureFlags as leaves.
 * Includes top-level keys and nested object keys (e.g., 'scopes.quipexApi', 'sessionConfig.idleTimeoutSeconds').
 */
type DotPath<T, Prefix extends string = ''> = {
    [K in keyof T & string]: T[K] extends readonly unknown[]
        ? `${Prefix}${K}`
        : T[K] extends Record<string, boolean>
          ? `${Prefix}${K}`
          : T[K] extends string | number | boolean
            ? `${Prefix}${K}`
            : T[K] extends object | null | undefined
              ? `${Prefix}${K}` | DotPath<NonNullable<T[K]>, `${Prefix}${K}.`>
              : `${Prefix}${K}`;
}[keyof T & string];

/**
 * Union of all valid dot-path keys for Environment fields.
 * Includes top-level keys and nested object keys, but treats arrays and featureFlags as leaves.
 */
type EnvironmentLabelKey = DotPath<Environment>;

/**
 * Message factory that can be a string or a function that takes a label and returns a string.
 */
type MessageFactory = string | ((label: string) => string);

/**
 * Value validator that takes a value and returns null if valid, or a MessageFactory if invalid.
 */
type ValueValidator = (value: unknown) => MessageFactory | null;

type Rule = {
    tier: Tier;
    labelKey: EnvironmentLabelKey;
    getValue: (env: Environment) => unknown;
    validate: ValueValidator;
    shouldValidate?: (env: Environment) => boolean;
    getMessage?: (label: string, env: Environment) => string;
};

/**
 * Friendly field labels for environment variables
 */
const FIELD_LABELS = {
    env: 'Environment',
    auth0ClientId: 'Auth0 Client ID',
    auth0Domain: 'Auth0 Domain',
    auth0Audience: 'Auth0 Audience',
    instrumentationKey: 'Instrumentation Key',
    apiBase: 'API Base URL',
    apiFunctionBase: 'API Function Base URL',
    apiAIBase: 'API AI Base URL',
    'scopes.quipexApi': 'Quipex API Scopes',
    sessionConfig: 'Session Config',
    'sessionConfig.inactiveTimeoutSeconds': 'Session Inactive Timeout',
    'sessionConfig.idleTimeoutSeconds': 'Session Idle Timeout',
    supportEmail: 'Support Email',
    'supportEmail.recipient': 'Support Email Recipient',
    'supportEmail.subject': 'Support Email Subject',
    maxFileSizeInMb: 'Max File Size',
    concurrentFilesUpload: 'Concurrent Files Upload',
    documentUploadTypes: 'Document Upload Types',
    imagePreviewFileTypes: 'Image Preview File Types',
    featureFlags: 'Feature Flags',
    dropboxClientId: 'Dropbox Client ID',
    googleClientId: 'Google Client ID',
    'datadogRum.applicationId': 'Datadog RUM Application ID',
    'datadogRum.clientToken': 'Datadog RUM Client Token',
    'datadogRum.defaultPrivacyLevel': 'Datadog RUM Default Privacy Level',
    'datadogRum.sessionReplaySampleRate':
        'Datadog RUM Session Replay Sample Rate',
    'datadogRum.sessionSampleRate': 'Datadog RUM Session Sample Rate',
    appVersion: 'App Version',
} satisfies Partial<Record<EnvironmentLabelKey, string>>;

const SOURCE_ENV_VARS = {
    env: 'NG_APP_ENV',
    auth0ClientId: 'NG_APP_AUTH0_CLIENT_ID',
    auth0Domain: 'NG_APP_AUTH0_DOMAIN',
    auth0Audience: 'NG_APP_AUTH0_AUDIENCE',
    instrumentationKey: 'NG_APP_INSTRUMENTATION_KEY',
    apiBase: 'NG_APP_API_BASE_URL',
    apiFunctionBase: 'NG_APP_API_FUNCTION_BASE_URL',
    apiAIBase: 'NG_APP_API_AI_BASE_URL',
    'scopes.quipexApi': 'NG_APP_SCOPES_QUIPEX',
    dropboxClientId: 'NG_APP_DROPBOX_CLIENT_ID',
    googleClientId: 'NG_APP_GOOGLE_CLIENT_ID',
    'datadogRum.applicationId': 'NG_APP_DATADOG_RUM_APPLICATION_ID',
    'datadogRum.clientToken': 'NG_APP_DATADOG_RUM_CLIENT_TOKEN',
    'datadogRum.defaultPrivacyLevel':
        'NG_APP_DATADOG_RUM_DEFAULT_PRIVACY_LEVEL',
    'datadogRum.sessionReplaySampleRate':
        'NG_APP_DATADOG_RUM_SESSION_REPLAY_SAMPLE_RATE',
    'datadogRum.sessionSampleRate': 'NG_APP_DATADOG_RUM_SESSION_SAMPLE_RATE',
    appVersion: 'NG_APP_VERSION',
} satisfies Partial<Record<EnvironmentLabelKey, string>>;

const requiredEnvValue: ValueValidator = (value) => {
    if (
        !value ||
        !['development', 'qa', 'uat', 'demo', 'production'].includes(
            value as string
        )
    ) {
        return (label) => `${label} is missing or invalid`;
    }
    return null;
};

const requiredString: ValueValidator = (value) => {
    if (!isNonEmptyString(value)) {
        return (label) => `${label} is missing or empty`;
    }
    if (containsDerivedUndefined(value)) {
        return (label) => `${label} is missing or empty`;
    }
    return null;
};

const requiredFiniteNumber: ValueValidator = (value) => {
    if (!isFiniteNumber(value)) {
        return (label) => `${label} must be a finite number`;
    }
    return null;
};

const requiredNonEmptyArray: ValueValidator = (value) => {
    if (!isNonEmptyArray(value)) {
        return (label) => `${label} must be a non-empty array`;
    }
    return null;
};

const requiredObject: ValueValidator = (value) => {
    if (
        value === null ||
        value === undefined ||
        typeof value !== 'object' ||
        Array.isArray(value)
    ) {
        return (label) => `${label} must be an object`;
    }
    return null;
};

const requiredScopeArray: ValueValidator = (value) => {
    if (!isNonEmptyArray<string>(value)) {
        return (label) => `${label} must be a non-empty array`;
    }
    for (const scope of value) {
        if (!isNonEmptyString(scope)) {
            return (label) => `${label} contains empty or invalid entries`;
        }
        if (containsDerivedUndefined(scope)) {
            return (label) =>
                `${label} contains derived undefined/null values (e.g., "api://undefined/...")`;
        }
    }
    return null;
};

const RULES: Rule[] = [
    // --- Tier 1: Critical (hard-stop) ---
    {
        tier: 'critical',
        labelKey: 'env',
        getValue: (e) => e.env,
        validate: requiredEnvValue,
    },
    {
        tier: 'critical',
        labelKey: 'auth0ClientId',
        getValue: (e) => e.auth0ClientId,
        validate: requiredString,
    },
    {
        tier: 'critical',
        labelKey: 'auth0Domain',
        getValue: (e) => e.auth0Domain,
        validate: requiredString,
    },
    {
        tier: 'critical',
        labelKey: 'auth0Audience',
        getValue: (e) => e.auth0Audience,
        validate: requiredString,
    },
    {
        tier: 'critical',
        labelKey: 'instrumentationKey',
        getValue: (e) => e.instrumentationKey,
        validate: requiredString,
    },
    {
        tier: 'critical',
        labelKey: 'apiBase',
        getValue: (e) => e.apiBase,
        validate: requiredString,
    },
    {
        tier: 'critical',
        labelKey: 'apiFunctionBase',
        getValue: (e) => e.apiFunctionBase,
        validate: requiredString,
    },
    {
        tier: 'critical',
        labelKey: 'apiAIBase',
        getValue: (e) => e.apiAIBase,
        validate: requiredString,
    },
    {
        tier: 'critical',
        labelKey: 'scopes.quipexApi',
        getValue: (e) => e.scopes?.quipexApi,
        validate: requiredScopeArray,
        getMessage: (label, env) =>
            createScopeArrayMessage((e) => e.scopes?.quipexApi, label)(env),
    },
    // --- Tier 2: Blocking (log + route to service-unavailable) ---
    {
        tier: 'blocking',
        labelKey: 'sessionConfig',
        getValue: (e) => e.sessionConfig,
        validate: (value) => {
            if (!value) {
                return (label) => `${label} is missing`;
            }
            return null;
        },
    },
    {
        tier: 'blocking',
        labelKey: 'sessionConfig.inactiveTimeoutSeconds',
        getValue: (e) => e.sessionConfig?.inactiveTimeoutSeconds,
        validate: requiredFiniteNumber,
    },
    {
        tier: 'blocking',
        labelKey: 'sessionConfig.idleTimeoutSeconds',
        getValue: (e) => e.sessionConfig?.idleTimeoutSeconds,
        validate: requiredFiniteNumber,
    },
    {
        tier: 'warning',
        labelKey: 'supportEmail',
        getValue: (e) => e.supportEmail,
        validate: requiredObject,
    },
    {
        tier: 'warning',
        labelKey: 'supportEmail.recipient',
        getValue: (e) => e.supportEmail?.recipient,
        validate: requiredString,
    },
    {
        tier: 'warning',
        labelKey: 'supportEmail.subject',
        getValue: (e) => e.supportEmail?.subject,
        validate: requiredString,
    },
    {
        tier: 'blocking',
        labelKey: 'maxFileSizeInMb',
        getValue: (e) => e.maxFileSizeInMb,
        validate: requiredFiniteNumber,
    },
    {
        tier: 'blocking',
        labelKey: 'concurrentFilesUpload',
        getValue: (e) => e.concurrentFilesUpload,
        validate: requiredFiniteNumber,
    },
    {
        tier: 'blocking',
        labelKey: 'documentUploadTypes',
        getValue: (e) => e.documentUploadTypes,
        validate: requiredNonEmptyArray,
    },
    {
        tier: 'blocking',
        labelKey: 'imagePreviewFileTypes',
        getValue: (e) => e.imagePreviewFileTypes,
        validate: requiredNonEmptyArray,
    },
    {
        tier: 'blocking',
        labelKey: 'featureFlags',
        getValue: (e) => e.featureFlags,
        validate: requiredObject,
    },
    {
        tier: 'blocking',
        labelKey: 'dropboxClientId',
        getValue: (e) => e.dropboxClientId,
        validate: requiredString,
    },
    {
        tier: 'blocking',
        labelKey: 'googleClientId',
        getValue: (e) => e.googleClientId,
        validate: requiredString,
    },
    {
        tier: 'blocking',
        labelKey: 'datadogRum.applicationId',
        getValue: (e) => e.datadogRum?.applicationId,
        validate: requiredString,
        shouldValidate: (e) => e.env !== 'development',
    },
    {
        tier: 'blocking',
        labelKey: 'datadogRum.clientToken',
        getValue: (e) => e.datadogRum?.clientToken,
        validate: requiredString,
        shouldValidate: (e) => e.env !== 'development',
    },
    // --- Warn-only (log but continue) ---
    {
        tier: 'warning',
        labelKey: 'datadogRum.defaultPrivacyLevel',
        getValue: (e) => e.datadogRum?.defaultPrivacyLevel,
        validate: requiredString,
        shouldValidate: (e) => e.env !== 'development',
    },
    {
        tier: 'warning',
        labelKey: 'datadogRum.sessionReplaySampleRate',
        getValue: (e) => e.datadogRum?.sessionReplaySampleRate,
        validate: requiredString,
        shouldValidate: (e) => e.env !== 'development',
    },
    {
        tier: 'warning',
        labelKey: 'datadogRum.sessionSampleRate',
        getValue: (e) => e.datadogRum?.sessionSampleRate,
        validate: requiredString,
        shouldValidate: (e) => e.env !== 'development',
    },
    {
        tier: 'warning',
        labelKey: 'appVersion',
        getValue: (e) => e.appVersion,
        validate: requiredString,
    },
];

/**
 * Validates runtime environment configuration with tiered error reporting.
 * Returns structured validation results for Tier 1 (critical), Tier 2 (blocking), and warnings.
 *
 * @param env - The environment object to validate
 * @returns Validation result with categorized errors and warnings
 */
export function validateRuntimeEnvironment(
    env: Environment
): EnvValidationResult {
    const criticalErrors: string[] = [];
    const blockingErrors: string[] = [];
    const warnings: string[] = [];

    for (const rule of RULES) {
        if (rule.shouldValidate && !rule.shouldValidate(env)) {
            continue;
        }
        const value = rule.getValue(env);
        const maybeMessage = rule.validate(value);

        if (maybeMessage !== null) {
            const label = getFieldDisplayName(rule.labelKey);
            const message = rule.getMessage
                ? rule.getMessage(label, env)
                : resolveMessage(maybeMessage, label);
            if (rule.tier === 'critical') {
                criticalErrors.push(message);
            } else if (rule.tier === 'blocking') {
                blockingErrors.push(message);
            } else {
                warnings.push(message);
            }
        }
    }

    return {
        criticalErrors,
        blockingErrors,
        warnings,
    };
}

function resolveMessage(messageFactory: MessageFactory, label: string): string {
    return typeof messageFactory === 'string'
        ? messageFactory
        : messageFactory(label);
}

function getFieldDisplayName(key: EnvironmentLabelKey): string {
    const label =
        (FIELD_LABELS as Partial<Record<EnvironmentLabelKey, string>>)[key] ??
        key;
    const envVar = (
        SOURCE_ENV_VARS as Partial<Record<EnvironmentLabelKey, string>>
    )[key];

    return envVar ? `${label} (${envVar})` : label;
}

function createScopeArrayMessage(
    getValue: (env: Environment) => unknown,
    label: string
): (env: Environment) => string {
    return (env: Environment) => {
        const scopes = getValue(env);
        if (!isNonEmptyArray<string>(scopes)) {
            return `${label} must be a non-empty array`;
        }
        for (const scope of scopes) {
            if (!isNonEmptyString(scope)) {
                return `${label} contains empty or invalid entries`;
            }
            if (containsDerivedUndefined(scope)) {
                return `${label} contains derived undefined/null values (e.g., "api://undefined/...")`;
            }
        }
        return `${label} must be a non-empty array`;
    };
}

function isNonEmptyString(value: unknown): value is string {
    return typeof value === 'string' && value.trim().length > 0;
}

function isFiniteNumber(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value);
}

function isNonEmptyArray<T>(value: unknown): value is T[] {
    return Array.isArray(value) && value.length > 0;
}

function containsDerivedUndefined(value: string): boolean {
    return value.includes('undefined') || value.includes('null');
}
